import {Popover, Spin, Tooltip} from "antd";
import React, {useEffect, useState} from "react";
import {DownloadOutlined} from "@ant-design/icons";
import {ApplicationDownloadableFiles} from "./model/ApplicationDownloadableFiles";
import {iif} from "../../../common/utils/functions";
import "./download-files.scss"
import DownloadReport from "../../../components/DownloadReport";
import {TooltipPlacement} from "antd/lib/tooltip";
import {ReviewAction, sendReviewEvent} from "../../../components/matomoClient";

type ComponentProps = {
    fetchAvailableDownloadableFiles: (appId: string, reviewId?: string) => Promise<ApplicationDownloadableFiles>
    appProps: AppProps,
    reviewProps: ReviewProps | null
    popoverPlacement: TooltipPlacement
    showOnlyLatestFiles: boolean
}

type AppProps = {
    id: string,
    name: string
}

type ReviewProps = {
    id: string,
    name: string,
    status: string
}

type DownloadContentProps = {
    applicationAvailableDownloads: ApplicationDownloadableFiles | null,
    isLoading: boolean
    error: string | null
    appProps: AppProps,
    reviewProps: ReviewProps | null
    showOnlyLatestFiles: boolean
}

function DownloadReportFiles(props: { reviewProps: ReviewProps, appProps: AppProps }): JSX.Element {
    return (
        <div className="application-review-stage">
            <div className="application-review-label">
                Review report
            </div>
            <div className="padding-container"/>
            <div className="downloadable-files">
                <div className="downloadable-file">
                    <div className="file-category">Report</div>
                    <div className="file-download-icon">
                        <DownloadReport
                            reviewId={props.reviewProps.id}
                            appId={props.appProps.id}
                            reviewName={props.reviewProps.name}
                            isSimpleButton={true}
                        />
                    </div>
                </div>
                <div className="padding-container"/>
            </div>
        </div>
    );
}

function DownloadableFilesContent(props: DownloadContentProps): JSX.Element {
    function canDownloadReviewReport(): boolean {
        return props.reviewProps !== null;
    }

    function areFilesAvailablForDownload(): boolean {
        return (props.applicationAvailableDownloads?.hasFiles() ?? false) || canDownloadReviewReport()
    }

    return (
        <div className="download-content-container">
            <div className="heading">Download - {props.appProps.name}</div>
            {props.isLoading ? <Spin className="spin-container"/> :
                props.error ? <div className="error-container">{props.error}</div> :
                    areFilesAvailablForDownload() ? null : <div>No files available for download</div>}
            {
                props.showOnlyLatestFiles ?
                    props.applicationAvailableDownloads?.renderLatestFiles(props.appProps.name) ?? null :
                    props.applicationAvailableDownloads?.render(props.appProps.name, props.reviewProps?.name ?? null) ?? null
            }
            {canDownloadReviewReport() ?
                <DownloadReportFiles reviewProps={props.reviewProps!} appProps={props.appProps}/>
                : null}
        </div>
    )
}

export function DownloadFiles(props: ComponentProps): JSX.Element {
    const [isDownloadContentVisible, setIsDownloadContentVisible] = useState<boolean>(false)
    const [isDownloadContentLoading, setIsDownloadContentLoading] = useState<boolean>(false)
    const [errorOnFetchingDownloadContent, setErrorOnFetchingDownloadContent] = useState<string | null>(null)
    const [applicationAvailableDownloads, setApplicationAvailableDownloads] = useState<ApplicationDownloadableFiles | null>(null)

    function onContentVisible(isVisible: boolean) {
        setIsDownloadContentVisible(isVisible)
        if (isVisible) {
            sendReviewEvent(ReviewAction.OpenedDownloadModal, props.reviewProps?.name ?? props.appProps.name)
        } else {
            sendReviewEvent(ReviewAction.ClosedDownloadModal, props.reviewProps?.name ?? props.appProps.name)
        }
    }

    function fetchDownloadableFiles() {
        if (isDownloadContentVisible) {
            iif(async () => {
                setIsDownloadContentLoading(true)
                try {
                    setApplicationAvailableDownloads(await props.fetchAvailableDownloadableFiles(props.appProps.id, props.reviewProps?.id))
                    setErrorOnFetchingDownloadContent(null)
                } catch (_err) {
                    setErrorOnFetchingDownloadContent("Failed to fetch contents")
                }
                setIsDownloadContentLoading(false)
            }).then()
        } else {
            setApplicationAvailableDownloads(null)
        }
    }

    useEffect(fetchDownloadableFiles, [isDownloadContentVisible])

    return (
        <Popover
            onVisibleChange={onContentVisible}
            trigger="click"
            placement={props.popoverPlacement}
            content={
                <DownloadableFilesContent
                    applicationAvailableDownloads={applicationAvailableDownloads}
                    isLoading={isDownloadContentLoading}
                    error={errorOnFetchingDownloadContent}
                    appProps={props.appProps}
                    reviewProps={props.reviewProps}
                    showOnlyLatestFiles={props.showOnlyLatestFiles}
                />
            }
        >
            <Tooltip title={"Download review files"} placement="left">
                <div className="download-icon">
                    <DownloadOutlined className={isDownloadContentVisible ? "popover-open" : "popover-closed"}/>
                </div>
            </Tooltip>
        </Popover>
    )
}